import { Injectable, inject } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import type { Ability } from '@core/abilities-types';
import { HttpApiService } from '@core/http-legacy';
import type { Role } from '@core/roles-types';

import type { RolePosition } from '../roles.types';

@Injectable({ providedIn: 'root' })
export class RoleHttpClient {
  protected readonly httpApi = inject(HttpApiService);

  public fetchRoles(ability: Ability | null = null): Observable<Role[]> {
    return this.httpApi.get(`/roles`, { ability }).pipe(map(data => data.roles));
  }

  public fetchRole(id: number | string) {
    return this.httpApi.get(`/roles/${id}`);
  }

  public updateRole(data: Partial<Role>) {
    return this.httpApi.patch(`/roles/${data.id}`, null, { role: data });
  }

  public createRole(data: Partial<Role>) {
    return this.httpApi.post(`/roles`, null, { role: data });
  }

  public deleteRole({ id, newRoleId }: { id: number; newRoleId: number }): Observable<Role> {
    return this.httpApi
      .delete(`/roles/${id}`, { new_role_id: newRoleId })
      .pipe(map(data => data.role));
  }

  public duplicateRole({ id }: { id: number }): Observable<Role> {
    return this.httpApi.patch(`/roles/${id}/duplicate`, null, {}).pipe(map(data => data.role));
  }

  public saveRoleAbilities(data: {
    id: number;
    abilities: Partial<Record<Ability, boolean>>;
  }): Observable<Role> {
    return this.httpApi
      .patch(`/roles/${data.id}`, null, { role: { abilities: data.abilities } })
      .pipe(map(data => data.role));
  }

  public updatePositions({ positions }: { positions: RolePosition[] }): Observable<Role[]> {
    return this.httpApi
      .patch(`/roles/update_positions`, null, {
        roles: positions,
      })
      .pipe(map(({ roles }) => roles));
  }

  public fetchAbilities(): Observable<Ability[]> {
    return this.httpApi
      .get('/roles/abilities', {}, { headers: { 'x-json-accent': 'snake' } })
      .pipe(map(({ abilities }) => <Ability[]>Object.keys(abilities)));
  }
}
